/**
 * Platform agent identities + keys, per
 * [[0028 - platform-shared-primitives-agents-suggestions-decisions-events]]
 * and the Agent Access management feature (Section B of the pre-launch plan).
 *
 * User-facing hooks target `/api/platform/agents` (workspace-scoped); the
 * admin "see-all" hooks target `/api/admin/agents` (cross-workspace, gated
 * server-side by requirePlatformAdmin).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend } from './client';

export type AgentStatus = 'active' | 'paused' | 'archived';

export interface AgentDto {
  id: string;
  label: string;
  description: string;
  status: AgentStatus;
  createdBy: string;
  createdAt: string;
  archivedAt: string | null;
}

export interface AgentKeyDto {
  id: string;
  agentId: string;
  label: string;
  /** Allowed app slugs; null = all apps (unrestricted). */
  appScope: string[] | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface AdminAgentDto {
  id: string;
  label: string;
  description: string;
  status: AgentStatus;
  workspaceId: string;
  workspaceName: string;
  ownerUserId: string | null;
  ownerEmail: string | null;
  keyCount: number;
  createdAt: string;
  archivedAt: string | null;
}

const keysKey = (agentId: string) => ['platform-agent-keys', agentId] as const;

/** List agents in the current workspace. Defaults to active-only. */
export function useAgents(options: { status?: AgentStatus } = {}) {
  const status = options.status ?? 'active';
  return useQuery({
    queryKey: ['platform-agents', { status }],
    queryFn: () =>
      apiGet<{ agents: AgentDto[] }>(
        `/platform/agents?status=${encodeURIComponent(status)}`,
      ),
    staleTime: 60_000,
  });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; description?: string }) =>
      apiSend<{ agent: AgentDto }>('POST', '/platform/agents', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-agents'] });
    },
  });
}

export function useUpdateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      patch: { label?: string; description?: string; status?: AgentStatus };
    }) =>
      apiSend<{ agent: AgentDto }>(
        'PATCH',
        `/platform/agents/${input.id}`,
        input.patch,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-agents'] });
    },
  });
}

export function useArchiveAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ agent: AgentDto }>('DELETE', `/platform/agents/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-agents'] });
    },
  });
}

/** List a single agent's active keys. */
export function useAgentKeys(agentId: string | null) {
  return useQuery({
    queryKey: keysKey(agentId ?? ''),
    queryFn: () =>
      apiGet<{ keys: AgentKeyDto[] }>(`/platform/agents/${agentId}/keys`),
    enabled: !!agentId,
    staleTime: 30_000,
  });
}

/**
 * Issue a key for an agent. Resolves to the response carrying the raw token
 * (shown to the operator EXACTLY ONCE). appScope omitted/empty = all apps.
 */
export function useIssueKey(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; appScope?: string[] | undefined }) =>
      apiSend<{ key: string; record: AgentKeyDto }>(
        'POST',
        `/platform/agents/${agentId}/keys`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keysKey(agentId) });
    },
  });
}

/** Edit a key's label / app scope without re-issuing the token. */
export function useEditKey(agentId: string, keyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label?: string; appScope?: string[] | null }) =>
      apiSend<{ record: AgentKeyDto }>(
        'PATCH',
        `/platform/agents/${agentId}/keys/${keyId}`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keysKey(agentId) });
    },
  });
}

/** Revoke a key (soft; idempotent). */
export function useRevokeKey(agentId: string, keyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiSend<{ ok: true }>('DELETE', `/platform/agents/${agentId}/keys/${keyId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keysKey(agentId) });
    },
  });
}

// ─── Admin (cross-workspace) ──────────────────────────────────────────

export interface AdminAgentFilters {
  workspaceId?: string | undefined;
  userId?: string | undefined;
  status?: AgentStatus | undefined;
}

export function useAdminAgents(filters: AdminAgentFilters = {}) {
  const params = new URLSearchParams();
  if (filters.workspaceId) params.set('workspaceId', filters.workspaceId);
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  return useQuery({
    queryKey: ['admin-agents', filters],
    queryFn: () =>
      apiGet<{ agents: AdminAgentDto[] }>(`/admin/agents${qs ? `?${qs}` : ''}`),
    staleTime: 15_000,
  });
}

/** Admin: update any agent's status/label (emergency pause), cross-workspace. */
export function useAdminUpdateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: { label?: string; status?: AgentStatus } }) =>
      apiSend<{ agent: AdminAgentDto }>(
        'PATCH',
        `/admin/agents/${input.id}`,
        input.patch,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-agents'] });
    },
  });
}

/** Admin: list any agent's keys (cross-workspace). */
export function useAdminAgentKeys(agentId: string | null) {
  return useQuery({
    queryKey: ['admin-agent-keys', agentId ?? ''],
    queryFn: () =>
      apiGet<{ keys: AgentKeyDto[] }>(`/admin/agents/${agentId}/keys`),
    enabled: !!agentId,
    staleTime: 15_000,
  });
}

/** Admin: revoke any agent key (cross-workspace kill switch). */
export function useAdminRevokeKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { agentId: string; keyId: string }) =>
      apiSend<{ ok: true }>(
        'DELETE',
        `/admin/agents/${input.agentId}/keys/${input.keyId}`,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-agents'] });
      qc.invalidateQueries({ queryKey: ['admin-agent-keys', vars.agentId] });
    },
  });
}

/** Admin: issue a key for any agent (cross-workspace). Raw token returned ONCE. */
export function useAdminIssueKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { agentId: string; label: string; appScope?: string[] | undefined }) =>
      apiSend<{ key: string; record: AgentKeyDto }>(
        'POST',
        `/admin/agents/${input.agentId}/keys`,
        { label: input.label, appScope: input.appScope },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-agents'] });
      qc.invalidateQueries({ queryKey: ['admin-agent-keys', vars.agentId] });
    },
  });
}
